import { useState } from 'react';
import { Loader2, LayoutTemplate } from 'lucide-react';
import { EntityModal } from '../common/EntityModal';
import { usePlanningTemplates, useClonePlanningList } from '../../hooks/usePlanning';

interface PlanningTemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Event the chosen template will be cloned into. */
  eventId: string;
  /** Default-filter templates to this event type; user can toggle to show all. */
  eventTypeId?: string;
  /** Called after a successful clone. */
  onCloned?: () => void;
}

export function PlanningTemplatePickerModal({
  open,
  onClose,
  eventId,
  eventTypeId,
  onCloned,
}: PlanningTemplatePickerModalProps) {
  const [showAll, setShowAll] = useState(false);
  const filters = showAll || !eventTypeId ? undefined : { eventTypeId };
  const { data: templates = [], isLoading } = usePlanningTemplates(filters);
  const clone = useClonePlanningList();

  function handleSelect(templateId: string) {
    clone.mutate(
      { id: templateId, eventId },
      {
        onSuccess: () => {
          onCloned?.();
          onClose();
        },
      },
    );
  }

  return (
    <EntityModal open={open} onClose={onClose} title="Use a Template" size="md">
      <div className="space-y-4">
        {eventTypeId && (
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            Show all event types
          </label>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-primary-600" size={24} />
          </div>
        ) : templates.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-400">No templates found.</div>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
            {templates.map((tpl) => (
              <li key={tpl.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(tpl.id)}
                  disabled={clone.isPending}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50"
                  data-testid={`template-${tpl.id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <LayoutTemplate size={16} className="text-primary-500 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">
                        {tpl.templateName ?? tpl.title ?? 'Untitled template'}
                      </span>
                      {tpl.eventTypeName && (
                        <span className="block text-xs text-gray-400 truncate">
                          {tpl.eventTypeName}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="text-xs text-gray-400 shrink-0">{tpl.itemCount} items</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </EntityModal>
  );
}
